import { useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCrud } from '../api/hooks/useCrud';
import { useAuthStore } from '../store/authStore';

// ═══════════════════════════════════════════════
// Tipos
// ═══════════════════════════════════════════════

export type EtapaStatus = 'pendente' | 'parcial' | 'concluida' | 'bloqueada';

export interface EtapaSetup {
  id: string;
  nome: string;
  descricao: string;
  status: EtapaStatus;
  total: number;
  meta: number;
  percentual: number;
  bloqueadoPor?: string;
  rota: string;
  responsaveis: string[];
  iconeKey: string;
}

export interface SetupStatus {
  progresso: number;
  prontoParaOperar: boolean;
  isFirstSetup: boolean;
  etapas: EtapaSetup[];
  pendencias: string[];
  etapasConcluidas: number;
  totalEtapas: number;
  counts: {
    equipe: number;
    turnos: number;
    materias: number;
    professores: number;
    turmas: number;
    responsaveis: number;
    alunos: number;
    horarios: number;
  };
}

/** Ordem do wizard (menu CONFIGURAÇÃO + passo final de grade). */
export const SETUP_SEQUENCE_IDS = [
  'equipe',
  'turnos',
  'materias',
  'professores',
  'turmas',
  'responsaveis',
  'alunos',
  'grade',
] as const;

/** Passos com item no menu lateral durante o setup (sem grade — grade fica em Turmas). */
export const SETUP_MENU_STEP_IDS = SETUP_SEQUENCE_IDS.filter((id) => id !== 'grade');

export function sortEtapasBySequence(etapas: EtapaSetup[]): EtapaSetup[] {
  const map = new Map(etapas.map((e) => [e.id, e]));
  const sorted: EtapaSetup[] = [];
  for (const id of SETUP_SEQUENCE_IDS) {
    const step = map.get(id);
    if (step) sorted.push(step);
  }
  for (const e of etapas) {
    if (!sorted.some((s) => s.id === e.id)) sorted.push(e);
  }
  return sorted;
}

/** Rotas admin → id da etapa (para guarda de navegação). */
export const ADMIN_SETUP_PATH_TO_ETAPA: Record<string, string> = {
  '/admin/equipe': 'equipe',
  '/admin/turnos': 'turnos',
  '/admin/materias': 'materias',
  '/admin/professores': 'professores',
  '/admin/turmas': 'turmas',
  '/admin/responsaveis': 'responsaveis',
  '/admin/alunos': 'alunos',
};

/**
 * Trava etapas futuras até concluir a atual (fluxo guiado passo a passo).
 * Etapas já concluídas permanecem acessíveis para revisão.
 */
export function applySequentialSetupLock(etapas: EtapaSetup[]): EtapaSetup[] {
  const clones = etapas.map((e) => ({ ...e }));
  const byId = Object.fromEntries(clones.map((e) => [e.id, e])) as Record<string, EtapaSetup>;
  const ordered = SETUP_SEQUENCE_IDS.map((id) => byId[id]).filter(
    (e): e is EtapaSetup => !!e,
  );

  const firstOpenIdx = ordered.findIndex((e) => e.status !== 'concluida');
  if (firstOpenIdx < 0) return clones;

  for (let j = firstOpenIdx + 1; j < ordered.length; j++) {
    const step = ordered[j];
    if (step.status === 'concluida') continue;
    step.status = 'bloqueada';
    step.bloqueadoPor = ordered[j - 1]?.nome ?? ordered[firstOpenIdx].nome;
    step.percentual = 0;
  }

  return clones;
}

// ═══════════════════════════════════════════════
// Hook principal
// ═══════════════════════════════════════════════

export function useSetupStatus(opts?: { routePrefix?: string; cargo?: string }): SetupStatus & { isLoading: boolean } {
  const routePrefix = opts?.routePrefix || '/admin';
  const filterCargo = opts?.cargo;
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId;

  const { data: turnos, isLoading: l1 } = useCrud<any>('turnos');
  const { data: materias, isLoading: l2 } = useCrud<any>('materias');
  const { data: turmas, isLoading: l3 } = useCrud<any>('turmas');
  const { data: professores, isLoading: l4 } = useCrud<any>('professores');
  const { data: alunos, isLoading: l5 } = useCrud<any>('alunos');
  const { data: equipe, isLoading: l6 } = useCrud<any>('equipe');
  const { data: horarios, isLoading: l7 } = useCrud<any>('horarios');
  const { data: responsaveis, isLoading: l8 } = useCrud<any>('responsaveis');

  const queryClient = useQueryClient();

  useEffect(() => {
    const handleGlobalRefetch = () => {
      queryClient.invalidateQueries({ queryKey: ['turnos'] });
      queryClient.invalidateQueries({ queryKey: ['materias'] });
      queryClient.invalidateQueries({ queryKey: ['turmas'] });
      queryClient.invalidateQueries({ queryKey: ['professores'] });
      queryClient.invalidateQueries({ queryKey: ['alunos'] });
      queryClient.invalidateQueries({ queryKey: ['equipe'] });
      queryClient.invalidateQueries({ queryKey: ['horarios'] });
      queryClient.invalidateQueries({ queryKey: ['responsaveis'] });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('ac-data-refetch', handleGlobalRefetch);
      return () => window.removeEventListener('ac-data-refetch', handleGlobalRefetch);
    }
  }, [queryClient]);

  const isLoading = l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8;

  const result = useMemo(() => {
    const tAtivos = turnos.filter((t: any) => t.ativo !== false);
    const mAtivas = materias.filter((m: any) => m.ativo !== false);
    const trAtivas = turmas.filter((t: any) => t.ativo !== false);
    const pAtivos = professores.filter((p: any) => p.ativo !== false);
    const aAtivos = alunos.filter((a: any) => a.ativo !== false);
    const rAtivos = responsaveis.filter((r: any) => r.ativo !== false);
    const eValidos = equipe; // Backend já filtra por escolaId; não filtrar por 'ativo' pois pode ser false mesmo com statusConta ATIVA

    // Equipe: ao adicionar 1 membro adicional (qualquer função/cargo), já libera o acesso e conclui a etapa
    const resolveRole = (u: any): string => {
      const p = (u.perfil || '').toUpperCase();
      const c = (u.cargo || '').toUpperCase();
      if (p.includes('COORD') || c.includes('COORD')) return 'COORDENACAO';
      if (p.includes('SEC') || c.includes('SEC')) return 'SECRETARIA';
      if (p.includes('DIR') || c.includes('DIR')) return 'DIRETOR';
      return u.perfil || 'ESCOLA';
    };
    const membrosNaoDiretor = eValidos.filter((u: any) => resolveRole(u) !== 'DIRETOR');
    const totalEquipe = eValidos.length;
    const temEquipeReal = eValidos.length >= 2 || membrosNaoDiretor.length >= 1;
    const statusEquipe = temEquipeReal ? 'concluida' : 'pendente';

    const todasEtapas: EtapaSetup[] = [
      {
        id: 'equipe',
        nome: 'Equipe',
        descricao: 'Convide ao menos um membro da equipe.',
        status: statusEquipe,
        total: Math.max(0, eValidos.length - 1),
        meta: 1,
        percentual: temEquipeReal ? 100 : 0,
        rota: `${routePrefix}/equipe`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR'],
        iconeKey: 'users',
      },
      {
        id: 'turnos',
        nome: 'Turnos',
        descricao: 'Manhã, Tarde e Noite — base das turmas.',
        status: (tAtivos.length > 0 || turnos.length > 0) ? 'concluida' : 'pendente',
        total: tAtivos.length || turnos.length,
        meta: 1,
        percentual: (tAtivos.length > 0 || turnos.length > 0) ? 100 : 0,
        rota: `${routePrefix}/turnos`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO'],
        iconeKey: 'clock',
      },
      {
        id: 'materias',
        nome: 'Matérias',
        descricao: 'Disciplinas oferecidas pela escola.',
        status: (mAtivas.length > 0 || materias.length > 0) ? 'concluida' : 'pendente',
        total: mAtivas.length || materias.length,
        meta: 1,
        percentual: (mAtivas.length > 0 || materias.length > 0) ? 100 : 0,
        rota: `${routePrefix}/materias`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO'],
        iconeKey: 'bookOpen',
      },
      {
        id: 'professores',
        nome: 'Professores',
        descricao: 'Docentes e vínculo com matérias.',
        status: (pAtivos.length > 0 || professores.length > 0)
          ? 'concluida'
          : ((mAtivas.length === 0 && materias.length === 0) ? 'bloqueada' : 'pendente'),
        total: pAtivos.length || professores.length,
        meta: 1,
        percentual: (pAtivos.length > 0 || professores.length > 0) ? 100 : 0,
        bloqueadoPor: (mAtivas.length === 0 && materias.length === 0) ? 'Matérias' : undefined,
        rota: `${routePrefix}/professores`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO'],
        iconeKey: 'userCheck',
      },
      {
        id: 'turmas',
        nome: 'Turmas',
        descricao: 'Turmas, turnos e capacidade por sala.',
        status: (trAtivas.length > 0 || turmas.length > 0)
          ? 'concluida'
          : ((tAtivos.length === 0 && turnos.length === 0) ? 'bloqueada' : 'pendente'),
        total: trAtivas.length || turmas.length,
        meta: 1,
        percentual: (trAtivas.length > 0 || turmas.length > 0) ? 100 : 0,
        bloqueadoPor: (tAtivos.length === 0 && turnos.length === 0) ? 'Turnos' : undefined,
        rota: `${routePrefix}/turmas`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO'],
        iconeKey: 'graduationCap',
      },
      {
        id: 'responsaveis',
        nome: 'Responsáveis',
        descricao: 'Pais e responsáveis com contato.',
        status: (rAtivos.length > 0 || responsaveis.length > 0) ? 'concluida' : 'pendente',
        total: rAtivos.length || responsaveis.length,
        meta: 1,
        percentual: (rAtivos.length > 0 || responsaveis.length > 0) ? 100 : 0,
        rota: `${routePrefix}/responsaveis`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'SECRETARIA'],
        iconeKey: 'heart',
      },
      {
        id: 'alunos',
        nome: 'Alunos',
        descricao: 'Matrículas nas turmas da escola.',
        status: (aAtivos.length > 0 || alunos.length > 0)
          ? 'concluida'
          : ((trAtivas.length === 0 && turmas.length === 0) ? 'bloqueada' : 'pendente'),
        total: aAtivos.length || alunos.length,
        meta: 1,
        percentual: (aAtivos.length > 0 || alunos.length > 0) ? 100 : 0,
        bloqueadoPor: (trAtivas.length === 0 && turmas.length === 0) ? 'Turmas' : undefined,
        rota: `${routePrefix}/alunos`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'SECRETARIA'],
        iconeKey: 'userPlus',
      },
      {
        id: 'grade',
        nome: 'Grade',
        descricao: 'Quadro semanal e horários de aula.',
        status:
          trAtivas.length === 0 || pAtivos.length === 0 || mAtivas.length === 0 || (aAtivos.length === 0 && alunos.length === 0)
            ? 'bloqueada'
            : (escolaId && localStorage.getItem(`ac_wizard_grade_vista_${escolaId}`) === 'true') || horarios.some((h: any) => h.materiaId)
              ? 'concluida'
              : 'pendente',
        total: (escolaId && localStorage.getItem(`ac_wizard_grade_vista_${escolaId}`) === 'true') || horarios.some((h: any) => h.materiaId) ? 1 : 0,
        meta: 1,
        percentual: ((escolaId && localStorage.getItem(`ac_wizard_grade_vista_${escolaId}`) === 'true') || horarios.some((h: any) => h.materiaId)) ? 100 : 0,
        bloqueadoPor:
          (aAtivos.length === 0 && alunos.length === 0)
            ? 'Alunos'
            : trAtivas.length === 0
              ? 'Turmas'
              : pAtivos.length === 0
                ? 'Professores'
                : mAtivas.length === 0
                  ? 'Matérias'
                  : undefined,
        rota: `${routePrefix}/setup?step=grade`,
        responsaveis: ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO'],
        iconeKey: 'calendar',
      },
    ];

    // Filtrar etapas por cargo de forma segura e determinística
    const normCargo = (filterCargo || '').toUpperCase();
    let etapasRaw = todasEtapas;
    if (normCargo.includes('COORD')) {
      etapasRaw = todasEtapas.filter((e) => e.responsaveis.includes('COORDENACAO'));
    } else if (normCargo.includes('SEC')) {
      etapasRaw = todasEtapas.filter((e) => e.responsaveis.includes('SECRETARIA'));
    } else if (normCargo.includes('DIR') || normCargo === 'ADMIN' || !filterCargo) {
      etapasRaw = todasEtapas;
    } else {
      const match = todasEtapas.filter((e) => e.responsaveis.includes(normCargo));
      etapasRaw = match.length > 0 ? match : todasEtapas;
    }

    const etapas = sortEtapasBySequence(applySequentialSetupLock(etapasRaw));


    const etapasConcluidas = etapas.filter((e) => e.status === 'concluida').length;
    const totalEtapas = etapas.length;
    const isFirstSetup = etapasConcluidas === 0;

    const storageKey = escolaId ? `ac_wizard_concluido_${escolaId}` : null;
    const storageKeyGrade = escolaId ? `ac_wizard_grade_vista_${escolaId}` : null;

    let prontoParaOperar = totalEtapas > 0 && etapasConcluidas === totalEtapas;

    // Se houver etapas pendentes, limpar o cache local para não mascarar o setup
    if (!prontoParaOperar && storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch { /* ignore */ }
    } else if (prontoParaOperar && storageKey) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch { /* ignore */ }
    }

    // Se não há alunos cadastrados, limpar a flag de grade vista para permitir testar o fluxo novamente
    if (aAtivos.length === 0 && alunos.length === 0 && storageKeyGrade) {
      try {
        localStorage.removeItem(storageKeyGrade);
      } catch { /* ignore */ }
    }

    const progresso = prontoParaOperar ? 100 : (totalEtapas > 0 ? Math.round((etapasConcluidas / totalEtapas) * 100) : 0);

    const pendencias: string[] = [];
    for (const e of etapas) {
      if (e.status === 'pendente') pendencias.push(`${e.nome}: nenhum cadastrado`);
      if (e.status === 'bloqueada') pendencias.push(`${e.nome}: requer ${e.bloqueadoPor}`);
    }

    const counts = {
      equipe: totalEquipe,
      turnos: tAtivos.length,
      materias: mAtivas.length,
      professores: pAtivos.length,
      turmas: trAtivas.length,
      responsaveis: rAtivos.length,
      alunos: aAtivos.length,
      horarios: horarios.length,
    };

    return {
      progresso,
      prontoParaOperar,
      isFirstSetup,
      etapas,
      pendencias,
      etapasConcluidas,
      totalEtapas,
      counts,
    };
  }, [turnos, materias, turmas, professores, alunos, responsaveis, equipe, horarios, routePrefix, filterCargo]);

  return { ...result, isLoading };
}
